import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface Product {
  id: string;
  name: string;
  description: string | null;
  price: number;
  original_price: number | null;
  rating: number;
  reviews_count: number;
  category: string;
  tags: string[];
  in_stock: boolean;
  main_image: string | null;
  images: string[];
  artistid: string | null;
  created_at: string;
  updated_at: string;
  sizes?: ProductSize[];
}

export interface ProductSize {
  id: string;
  product_id: string;
  size_name: string;
  size_display_name: string;
  price: number;
  original_price: number | null;
  is_available: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface CreateProductData {
  name: string;
  description?: string;
  price: number;
  original_price?: number;
  category: string;
  tags?: string[];
  in_stock?: boolean;
  main_image?: string;
  images?: string[];
  rating?: number;
  reviews_count?: number;
  artistid?: string;
}

export interface UpdateProductData extends Partial<CreateProductData> {
  id: string;
}

export function useProducts() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data, error } = await supabase
        .from("products")
        .select(`
          *,
          sizes:product_sizes(*)
        `)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setProducts(data || []);
    } catch (err) {
      console.error("Error fetching products:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch products");
    } finally {
      setLoading(false);
    }
  };

  const createProduct = async (productData: CreateProductData): Promise<Product | null> => {
    try {
      setError(null);
      
      console.log("Creating product with data:", productData);
      
      const insertData = {
        ...productData,
        rating: productData.rating ?? 0,
        reviews_count: productData.reviews_count ?? 0,
        tags: productData.tags || [],
        images: productData.images || [],
        in_stock: productData.in_stock ?? true,
        artistid: productData.artistid || null,
      };
      
      console.log("Insert data:", insertData);
      
      // Use admin client to bypass RLS policies
      const { data, error } = await supabase
        .from("products")
        .insert([insertData])
        .select()
        .single();

      if (error) {
        console.error("Supabase error:", error);
        console.error("Error details:", {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint
        });
        throw error;
      }
      
      console.log("Product created successfully:", data);
      
      // Update local state
      setProducts(prev => [data, ...prev]);
      return data;
    } catch (err) {
      console.error("Error creating product:", err);
      setError(err instanceof Error ? err.message : "Failed to create product");
      return null;
    }
  };

  const updateProduct = async (productData: UpdateProductData): Promise<Product | null> => {
    try {
      setError(null);
      
      const { id, ...updateData } = productData;
      
      console.log("Updating product with data:", { id, updateData });
      
      // Use admin client to bypass RLS policies
      const { data, error } = await supabase
        .from("products")
        .update(updateData)
        .eq("id", id)
        .select()
        .single();

      if (error) {
        console.error("Supabase update error:", error);
        console.error("Error details:", {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint
        });
        throw error;
      }
      
      console.log("Product updated successfully:", data);
      
      // Update local state
      setProducts(prev => prev.map(p => p.id === id ? data : p));
      return data;
    } catch (err) {
      console.error("Error updating product:", err);
      setError(err instanceof Error ? err.message : "Failed to update product");
      return null;
    }
  };

  const deleteProduct = async (productId: string): Promise<boolean> => {
    try {
      setError(null);
      
      // Use admin client to bypass RLS policies
      const { error } = await supabase
        .from("products")
        .delete()
        .eq("id", productId);

      if (error) throw error;
      
      // Update local state
      setProducts(prev => prev.filter(p => p.id !== productId));
      return true;
    } catch (err) {
      console.error("Error deleting product:", err);
      setError(err instanceof Error ? err.message : "Failed to delete product");
      return false;
    }
  };

  const getProductById = async (productId: string): Promise<Product | null> => {
    try {
      setError(null);
      
      const { data, error } = await supabase
        .from("products")
        .select("*")
        .eq("id", productId)
        .single();

      if (error) throw error;
      return data;
    } catch (err) {
      console.error("Error fetching product:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch product");
      return null;
    }
  };

  const searchProducts = async (searchTerm: string): Promise<Product[]> => {
    try {
      setError(null);
      
      const { data, error } = await supabase
        .from("products")
        .select("*")
        .or(`name.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%`)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (err) {
      console.error("Error searching products:", err);
      setError(err instanceof Error ? err.message : "Failed to search products");
      return [];
    }
  };

  const getProductsByCategory = async (category: string): Promise<Product[]> => {
    try {
      setError(null);
      
      const { data, error } = await supabase
        .from("products")
        .select("*")
        .eq("category", category)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (err) {
      console.error("Error fetching products by category:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch products by category");
      return [];
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  return {
    products,
    loading,
    error,
    fetchProducts,
    createProduct,
    updateProduct,
    deleteProduct,
    getProductById,
    searchProducts,
    getProductsByCategory,
  };
} 